/* eslint-disable react-refresh/only-export-components -- bewusst co-lozierter Provider+Hook bzw. Helfer-Export; react-refresh ist eine reine Dev-HMR-Optimierung, kein Prod-/Qualitaetsthema. */
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/cn';

/**
 * Ignis AI Splash-Screen — 8-Phasen-Animation gemäß Storyboard
 * (siehe `docs/spec/splash-storyboard.jpg`).
 *
 * Reihenfolge: Raster → Building-Wireframe → Gelbe Flächen →
 * Flamme → Hexagon-Schutz → Logo komplettiert → Brand-Signature →
 * Fade-Out zur Hauptanwendung.
 *
 * Pro Session nur einmal: ein sessionStorage-Flag verhindert, dass
 * der Splash bei Tab-Wechsel oder Reload nochmal erscheint.
 */

const STORAGE_KEY = 'ignisai.splash.seen.v1';

interface Props {
  /** Wird gerufen wenn die Animation fertig ist oder der User skippt. */
  onDone: () => void;
  /** Gesamtdauer in ms (default 4500). */
  duration?: number;
}

export function SplashScreen({ onDone, duration = 4500 }: Props) {
  const [phase, setPhase] = useState(0);
  const [exiting, setExiting] = useState(false);

  // Phasen-Sequenz: 0=Raster, 1=Wireframe, 2=Flächen, 3=Flamme,
  // 4=Hexagon, 5=Logo, 6=Claim, 7=FadeOut
  useEffect(() => {
    const stepMs = duration / 8;
    const timers: number[] = [];
    for (let i = 1; i <= 7; i++) {
      timers.push(window.setTimeout(() => setPhase(i), stepMs * i));
    }
    timers.push(
      window.setTimeout(() => {
        setExiting(true);
        window.setTimeout(() => {
          sessionStorage.setItem(STORAGE_KEY, '1');
          onDone();
        }, 450);
      }, duration),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [duration, onDone]);

  function skip() {
    setExiting(true);
    sessionStorage.setItem(STORAGE_KEY, '1');
    window.setTimeout(onDone, 250);
  }

  return (
    <AnimatePresence>
      {!exiting ? (
        <motion.div
          key="splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          // text-white statt text-paper: der Splash ist immer navy-dunkel,
          // `paper` spiegelt dagegen mit dem Theme und waere im Dunkelmodus
          // dunkelnavy auf dunkelnavy.
          className="fixed inset-0 z-[200] flex items-center justify-center overflow-hidden bg-navy-950 text-white"
        >
          {/* Skip-Button */}
          <button
            type="button"
            onClick={skip}
            className="absolute right-4 top-4 z-10 rounded border border-navy-700/60 bg-navy-900/40 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-navy-300 backdrop-blur-sm hover:border-navy-500 hover:text-white"
          >
            Überspringen
          </button>

          <SplashStage phase={phase} />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

/**
 * Helper: läuft der Splash in dieser Session schon einmal? Aufruf vor
 * dem ersten Render entscheidet, ob er überhaupt gemountet wird.
 */
export function hasSeenSplash(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return true; // im Zweifel überspringen
  }
}

// ============================================================
// Stage — die eigentliche Komposition
// ============================================================

function SplashStage({ phase }: { phase: number }) {
  return (
    <div className="relative h-[520px] w-[360px] sm:h-[600px] sm:w-[420px]">
      {/* Phase 1: Raster */}
      <Raster active={phase >= 0} fading={phase >= 6} />

      {/* Phase 2: Building-Wireframe */}
      <Building active={phase >= 1} fading={phase >= 5} />

      {/* Phase 3: Gelbe Flächen */}
      <BuildingFill active={phase >= 2} fading={phase >= 5} />

      {/* Phase 4: Flamme */}
      <Flame active={phase >= 3} settled={phase >= 5} />

      {/* Phase 5: Hexagon-Schutz */}
      <HexShield active={phase >= 4} settled={phase >= 5} />

      {/* Phase 6: Wortmarke */}
      <Wordmark active={phase >= 5} />

      {/* Phase 7: Brand Signature */}
      <Signature active={phase >= 6} />
    </div>
  );
}

// ------------------------------------------------------------
// Phase 1 — Raster
// ------------------------------------------------------------

function Raster({ active, fading }: { active: boolean; fading: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: active ? (fading ? 0.15 : 0.45) : 0 }}
      transition={{ duration: 0.6 }}
      className={cn(
        'pointer-events-none absolute inset-0',
        'bg-[linear-gradient(to_right,rgba(47,118,181,0.18)_1px,transparent_1px),linear-gradient(to_bottom,rgba(47,118,181,0.18)_1px,transparent_1px)] bg-[length:32px_32px]',
      )}
      aria-hidden
    />
  );
}

// ------------------------------------------------------------
// Phase 2 — Building wireframe
// ------------------------------------------------------------

function Building({ active, fading }: { active: boolean; fading: boolean }) {
  // Building skyline: drei Türme unterschiedlicher Höhe als Wireframe.
  return (
    <motion.svg
      viewBox="0 0 360 520"
      className="absolute inset-0 h-full w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: active ? (fading ? 0 : 1) : 0 }}
      transition={{ duration: 0.5 }}
      aria-hidden
    >
      <g stroke="currentColor" strokeWidth="1.5" fill="none" className="text-accent-400/60">
        {/* Turm links */}
        <motion.path
          d="M70 360 L70 220 L140 220 L140 360 Z"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: active ? 1 : 0 }}
          transition={{ duration: 0.7, delay: 0 }}
        />
        {/* Turm Mitte (höher) */}
        <motion.path
          d="M150 360 L150 160 L230 160 L230 360 Z"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: active ? 1 : 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
        />
        {/* Turm rechts */}
        <motion.path
          d="M240 360 L240 200 L300 200 L300 360 Z"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: active ? 1 : 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
        />
        {/* Boden / Horizontlinie */}
        <motion.path
          d="M40 360 L320 360"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: active ? 1 : 0 }}
          transition={{ duration: 0.5 }}
        />
      </g>
    </motion.svg>
  );
}

// ------------------------------------------------------------
// Phase 3 — Building gefüllt
// ------------------------------------------------------------

function BuildingFill({ active, fading }: { active: boolean; fading: boolean }) {
  return (
    <motion.svg
      viewBox="0 0 360 520"
      className="absolute inset-0 h-full w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: active ? (fading ? 0 : 0.7) : 0 }}
      transition={{ duration: 0.6 }}
      aria-hidden
    >
      <defs>
        <linearGradient id="bldg" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#3E6CA8" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#3E6CA8" stopOpacity="0.3" />
        </linearGradient>
      </defs>
      <motion.path
        d="M70 360 L70 220 L140 220 L140 360 Z"
        fill="url(#bldg)"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: active ? 1 : 0 }}
        transition={{ duration: 0.5, delay: 0 }}
        style={{ transformOrigin: '105px 360px' }}
      />
      <motion.path
        d="M150 360 L150 160 L230 160 L230 360 Z"
        fill="url(#bldg)"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: active ? 1 : 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        style={{ transformOrigin: '190px 360px' }}
      />
      <motion.path
        d="M240 360 L240 200 L300 200 L300 360 Z"
        fill="url(#bldg)"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: active ? 1 : 0 }}
        transition={{ duration: 0.5, delay: 0.18 }}
        style={{ transformOrigin: '270px 360px' }}
      />
    </motion.svg>
  );
}

// ------------------------------------------------------------
// Phase 4 — Flamme
// ------------------------------------------------------------

function Flame({ active, settled }: { active: boolean; settled: boolean }) {
  // Stilisierte Flamme rechts neben Building, wandert in Phase 5 ins Logo.
  return (
    <motion.svg
      viewBox="0 0 360 520"
      className="absolute inset-0 h-full w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: active ? 1 : 0 }}
      transition={{ duration: 0.4 }}
      aria-hidden
    >
      <defs>
        <radialGradient id="flameGlow" cx="50%" cy="60%" r="55%">
          <stop offset="0%" stopColor="#3E6CA8" stopOpacity="1" />
          <stop offset="60%" stopColor="#3E6CA8" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#3E6CA8" stopOpacity="0" />
        </radialGradient>
      </defs>
      <motion.g
        initial={{ x: 0, y: 0, scale: 0.6, opacity: 0 }}
        animate={
          settled
            ? { x: -15, y: -45, scale: 0.65, opacity: 1 }
            : active
              ? { x: 0, y: 0, scale: 1, opacity: 1 }
              : { opacity: 0 }
        }
        transition={{ duration: 0.7, ease: 'easeOut' }}
      >
        {/* Aura */}
        <circle cx="285" cy="290" r="60" fill="url(#flameGlow)" />
        {/* Flamme */}
        <path
          d="M285 230
             C 270 250 260 270 263 290
             C 265 305 275 315 285 320
             C 295 315 305 305 307 290
             C 310 270 300 250 285 230 Z"
          fill="#3E6CA8"
        />
        <path
          d="M285 250
             C 278 262 273 278 278 292
             C 281 300 286 305 285 312
             C 290 305 295 298 296 290
             C 298 278 293 264 285 250 Z"
          fill="#E3E9EF"
          opacity="0.85"
        />
      </motion.g>
    </motion.svg>
  );
}

// ------------------------------------------------------------
// Phase 5 — Hexagon-Schutz
// ------------------------------------------------------------

function HexShield({ active, settled }: { active: boolean; settled: boolean }) {
  // Hexagon zeichnet sich um Building+Flamme; in Phase 5 schrumpft alles zum Logo.
  return (
    <motion.svg
      viewBox="0 0 360 520"
      className="absolute inset-0 h-full w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: active ? 1 : 0 }}
      transition={{ duration: 0.4 }}
      aria-hidden
    >
      <motion.path
        d="M180 100 L300 170 L300 310 L180 380 L60 310 L60 170 Z"
        fill="none"
        stroke="#cfd5dd"
        strokeWidth="3"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={
          settled
            ? { pathLength: 1, opacity: 1, scale: 0.55, y: -40 }
            : active
              ? { pathLength: 1, opacity: 1, scale: 1, y: 0 }
              : { pathLength: 0, opacity: 0 }
        }
        transition={{ duration: 0.85, ease: 'easeInOut' }}
        style={{ transformOrigin: '180px 240px' }}
      />
    </motion.svg>
  );
}

// ------------------------------------------------------------
// Phase 6 — Wortmarke Ignis AI
// ------------------------------------------------------------

function Wordmark({ active }: { active: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: active ? 1 : 0, y: active ? 0 : 20 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="absolute left-0 right-0 top-[58%] flex flex-col items-center"
      aria-hidden
    >
      <h1 className="text-5xl font-bold tracking-tight text-white sm:text-6xl">
        Ignis<span className="text-accent-500"> AI</span>
      </h1>
    </motion.div>
  );
}

// ------------------------------------------------------------
// Phase 7 — Brand Signature
// ------------------------------------------------------------

function Signature({ active }: { active: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: active ? 1 : 0, y: active ? 0 : 12 }}
      transition={{ duration: 0.7, ease: 'easeOut', delay: 0.05 }}
      className="absolute left-0 right-0 top-[72%] flex flex-col items-center gap-1"
      aria-hidden
    >
      <p className="text-xs font-medium uppercase tracking-[0.4em] text-navy-400">
        Brandschutz · Intelligent geplant
      </p>
      <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-navy-500">
        Ignis AI — Brandschutzmanagement
      </p>
    </motion.div>
  );
}
